/**
 * Jovii — display formatting.
 *
 * Owned by the CATALOGUE UI agent. Pure functions, no state, no DOM.
 * Everything here answers one question: what does a human read?
 */

import type { Candidate, MediaTrack, ProbeFailure, ProbedSource } from '$types';

/* ── sizes and rates ─────────────────────────────────────────────────────── */

/** "4.5 GB". Bytes are decimal GB here because that is what release names use. */
export function fileSize(bytes: number): string {
  if (!bytes || bytes < 0) return '';
  if (bytes >= 1e9) return `${(bytes / 1e9).toFixed(bytes >= 1e10 ? 0 : 1)} GB`;
  if (bytes >= 1e6) return `${Math.round(bytes / 1e6)} MB`;
  return `${Math.round(bytes / 1e3)} kB`;
}

/** "8.2 Mbps". */
export function bitrate(bps: number): string {
  if (!bps || bps < 0) return '';
  if (bps >= 1e6) return `${(bps / 1e6).toFixed(1)} Mbps`;
  return `${Math.round(bps / 1e3)} kbps`;
}

/* ── time ────────────────────────────────────────────────────────────────── */

/** Seconds → "1:23:45" or "23:45". Used for durations and positions alike. */
export function clock(totalSec: number): string {
  if (!Number.isFinite(totalSec) || totalSec < 0) return '0:00';
  const s = Math.floor(totalSec % 60);
  const m = Math.floor(totalSec / 60) % 60;
  const h = Math.floor(totalSec / 3600);
  const mm = h > 0 ? String(m).padStart(2, '0') : String(m);
  return h > 0 ? `${h}:${mm}:${String(s).padStart(2, '0')}` : `${mm}:${String(s).padStart(2, '0')}`;
}

/** Seconds → "2h 22m" / "48m". For runtimes, where precision is noise. */
export function runtime(totalSec: number): string {
  if (!Number.isFinite(totalSec) || totalSec <= 0) return '';
  const m = Math.round(totalSec / 60);
  const h = Math.floor(m / 60);
  return h > 0 ? `${h}h ${String(m % 60).padStart(2, '0')}m` : `${m}m`;
}

/** "22m left" for a resume point. */
export function remaining(positionSec: number, durationSec: number): string {
  const left = Math.max(0, durationSec - positionSec);
  if (left <= 0) return '';
  const m = Math.round(left / 60);
  if (m < 1) return 'seconds left';
  const h = Math.floor(m / 60);
  return h > 0 ? `${h}h ${String(m % 60).padStart(2, '0')}m left` : `${m}m left`;
}

/** ISO date → "21 Jan 2008". Empty string when unparseable; never "Invalid Date". */
export function airDate(iso: string | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

/** "3 days ago" / "just now". For resume points and probe ages. */
export function ago(ms: number): string {
  const delta = Date.now() - ms;
  if (delta < 60_000) return 'just now';
  const mins = Math.round(delta / 60_000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(ms).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

/** "S1 E4". */
export function episodeCode(season: number, episode: number): string {
  return `S${season} E${episode}`;
}

/* ── languages ───────────────────────────────────────────────────────────── */

const LANGS: Record<string, string> = {
  en: 'English',
  eng: 'English',
  fr: 'French',
  fre: 'French',
  fra: 'French',
  es: 'Spanish',
  spa: 'Spanish',
  de: 'German',
  ger: 'German',
  deu: 'German',
  it: 'Italian',
  ita: 'Italian',
  pt: 'Portuguese',
  por: 'Portuguese',
  ru: 'Russian',
  rus: 'Russian',
  ja: 'Japanese',
  jpn: 'Japanese',
  ko: 'Korean',
  kor: 'Korean',
  zh: 'Chinese',
  chi: 'Chinese',
  zho: 'Chinese',
  hi: 'Hindi',
  hin: 'Hindi',
  ar: 'Arabic',
  ara: 'Arabic',
  nl: 'Dutch',
  dut: 'Dutch',
  nld: 'Dutch',
  pl: 'Polish',
  pol: 'Polish',
  sv: 'Swedish',
  swe: 'Swedish',
  no: 'Norwegian',
  nor: 'Norwegian',
  da: 'Danish',
  dan: 'Danish',
  fi: 'Finnish',
  fin: 'Finnish',
  tr: 'Turkish',
  tur: 'Turkish',
  cs: 'Czech',
  ces: 'Czech',
  el: 'Greek',
  ell: 'Greek',
  he: 'Hebrew',
  heb: 'Hebrew',
  th: 'Thai',
  tha: 'Thai',
  uk: 'Ukrainian',
  ukr: 'Ukrainian',
  hu: 'Hungarian',
  hun: 'Hungarian',
  ro: 'Romanian',
  ron: 'Romanian',
  multi: 'Multi',
  und: 'Unknown'
};

/** Tag → readable name, falling back to Intl and then to the tag itself. */
export function language(tag: string): string {
  if (!tag) return '';
  const key = tag.toLowerCase().trim();
  if (LANGS[key]) return LANGS[key];
  const short = key.slice(0, 3);
  if (LANGS[short]) return LANGS[short];
  try {
    const dn = new Intl.DisplayNames([navigator.language || 'en'], { type: 'language' });
    const name = dn.of(key.slice(0, 2));
    if (name && name.toLowerCase() !== key.slice(0, 2)) return name;
  } catch {
    /* Intl.DisplayNames missing or given a tag it cannot parse */
  }
  return tag.toUpperCase();
}

/** Compact language list for a candidate row: "English, French +2". */
export function languageList(tags: string[], max = 2): string {
  const seen: string[] = [];
  for (const t of tags) {
    const name = language(t);
    if (name && !seen.includes(name)) seen.push(name);
  }
  if (seen.length === 0) return '';
  const head = seen.slice(0, max).join(', ');
  return seen.length > max ? `${head} +${seen.length - max}` : head;
}

/* ── codecs and tracks ───────────────────────────────────────────────────── */

const CODEC_NAMES: Record<string, string> = {
  h264: 'H.264',
  avc1: 'H.264',
  hevc: 'HEVC',
  h265: 'HEVC',
  av1: 'AV1',
  vp9: 'VP9',
  mpeg4: 'MPEG-4',
  aac: 'AAC',
  ac3: 'AC-3',
  eac3: 'E-AC-3',
  dts: 'DTS',
  dca: 'DTS',
  truehd: 'TrueHD',
  mlp: 'TrueHD',
  flac: 'FLAC',
  opus: 'Opus',
  vorbis: 'Vorbis',
  mp3: 'MP3',
  alac: 'ALAC',
  subrip: 'SRT',
  ass: 'ASS',
  ssa: 'SSA',
  webvtt: 'VTT',
  mov_text: 'Text',
  hdmv_pgs_subtitle: 'PGS',
  dvd_subtitle: 'VobSub'
};

export function codecName(codec: string): string {
  return CODEC_NAMES[codec.toLowerCase()] ?? codec.toUpperCase();
}

/** "5.1" / "2.0" / "7.1" from a channel count. */
export function channelLayout(channels: number | undefined): string {
  if (!channels) return '';
  if (channels === 1) return 'Mono';
  if (channels === 2) return 'Stereo';
  if (channels === 6) return '5.1';
  if (channels === 8) return '7.1';
  return `${channels}ch`;
}

/**
 * "1080p" from the real frame size, so the probe can contradict the release name.
 *
 * WIDTH FIRST. Height alone lies about films: a 2.40:1 scope transfer is 1920×800,
 * and bucketing on height called every one of them "720p" — in the link picker, on
 * the row whose whole job is to tell you which file is better, right next to a
 * scraper badge saying `1080P` and a setting called "Prefer up to 1080p". The
 * player's Details sheet said 1080p for the same file in the same session, because
 * it used width. This is that same rule; there is now one of them.
 */
export function resolutionLabel(track: MediaTrack | undefined): string {
  const w = track?.widthPx ?? 0;
  const h = track?.heightPx ?? 0;
  if (!w && !h) return '';
  if (w >= 3400 || h >= 2000) return '2160p';
  if (w >= 1800 || h >= 1000) return '1080p';
  if (w >= 1200 || h >= 700) return '720p';
  if (w >= 640 || h >= 400) return '576p';
  return h > 0 ? `${h}p` : '';
}

export interface TrackSummary {
  video: MediaTrack | undefined;
  audio: MediaTrack[];
  subtitles: MediaTrack[];
  /** "H.264 1080p" */
  videoLine: string;
  /** "DTS 5.1 → AAC" or "AAC Stereo" */
  audioLine: string;
  /** True when the chosen-by-default audio needs a client-side transcode. */
  transcodes: boolean;
}

/** What the probe found, in the words the picker shows. */
export function summarise(probe: ProbedSource): TrackSummary {
  const video = probe.tracks.find((t) => t.kind === 'video');
  const audio = probe.tracks.filter((t) => t.kind === 'audio');
  const subtitles = probe.tracks.filter((t) => t.kind === 'subtitle');
  const first = audio.find((t) => t.isDefault) ?? audio[0];

  const videoLine = video
    ? [codecName(video.codec), resolutionLabel(video)].filter(Boolean).join(' ')
    : 'no video';

  let audioLine = '';
  if (first) {
    const base = [codecName(first.codec), channelLayout(first.channels)].filter(Boolean).join(' ');
    audioLine = first.passthrough ? base : `${base} → AAC`;
  }

  return {
    video,
    audio,
    subtitles,
    videoLine,
    audioLine,
    transcodes: first ? !first.passthrough : false
  };
}

/* ── failures ────────────────────────────────────────────────────────────── */

/** A probe failure in plain words. Names the problem, not the enum. */
export function failureText(failure: ProbeFailure | undefined): string {
  switch (failure) {
    case 'placeholder':
      return 'Decoy file — not the film';
    case 'not-media':
      return 'Not a video file';
    case 'no-video-track':
      return 'No video inside';
    case 'unsupported-video-codec':
      return 'This device cannot decode it';
    case 'no-range-support':
      return 'Server will not seek';
    case 'http-error':
      return 'Link is dead';
    case 'timeout':
      return 'Too slow to answer';
    case 'aborted':
      return 'Cancelled';
    default:
      return 'Would not open';
  }
}

/* ── candidates ──────────────────────────────────────────────────────────── */

const PROVIDER_NAMES: Record<string, string> = {
  torrentio: 'Torrentio',
  mediafusion: 'MediaFusion',
  other: 'Other'
};

export function providerName(p: string): string {
  return PROVIDER_NAMES[p] ?? p;
}

/**
 * The tracker/source line under a candidate title. `originLabel` already reads
 * "Torrentio · ThePirateBay · RD"; strip the leading provider so the row does not
 * say it twice next to the provider chip.
 */
export function originText(c: Candidate): string {
  const label = c.originLabel ?? '';
  const prefix = `${providerName(c.provider)} · `;
  return label.startsWith(prefix) ? label.slice(prefix.length) : label;
}

/**
 * Release names are unreadable at a glance. Pull the parts that matter and drop
 * the scene furniture, but never hide the original — the picker shows both.
 */
export function releaseTags(c: Candidate): string[] {
  const tags: string[] = [];
  if (c.quality !== 'unknown') tags.push(c.quality);
  const source = /\b(bluray|blu-ray|remux|web-?dl|webrip|hdtv|dvdrip|bdrip|brrip)\b/i.exec(c.title);
  if (source?.[1]) tags.push(source[1].toUpperCase().replace('BLU-RAY', 'BLURAY'));
  if (/\bhdr10\+|\bhdr\b|\bdolby ?vision\b|\bdv\b/i.test(c.title)) tags.push('HDR');
  if (/\batmos\b/i.test(c.title)) tags.push('ATMOS');
  for (const hint of c.audioHints) if (!tags.includes(hint)) tags.push(hint);
  return tags.slice(0, 4);
}

/** Plural helper: `count(3, 'episode')` → "3 episodes". */
export function count(n: number, noun: string, plural = `${noun}s`): string {
  return `${n} ${n === 1 ? noun : plural}`;
}
